/**
 * The room the orchestra is heard in, and the last stage before the speakers.
 *
 * The recordings are close and dry, and fifteen of them played straight into the speakers
 * are fifteen instruments in fifteen cupboards, all in the same spot. An orchestra is heard
 * as one because it is heard in a hall and spread across a stage, so each instrument sits on
 * its own seat -- a stereo position -- and every seat sends part of its sound into a
 * reverberation, while all of it also goes on dry. The hall is made here, from noise, rather
 * than fetched: a decaying, darkening burst of noise is what a hall's echo sounds like, and
 * it is made from a fixed seed so the same score always renders to the same file.
 *
 * Then the level. Measured before this stage existed, fifteen parts struck *ff* together
 * summed to +6.5 dB over full scale, the limiter pulled 4 to 10 dB off them -- heard as the
 * whole orchestra pumping -- and 439 samples still went over and were clipped by the
 * speakers. So the sum is turned down first by {@link TRIM}, which leaves every balance and
 * every *pp* to *ff* as it was, and only then limited. A limiter reacts in milliseconds, so
 * what slips through its attack is rounded off by a soft clipper instead of being cut flat.
 */

/** A seat on the stage: its stereo position, from -1 (left) to 1 (right). */
export type Seat = number;

/** Seconds the hall's echo lasts. */
export const RING = 2.2;

/** Seconds before the first echo, which is what makes a room sound big rather than muddy. */
const PREDELAY = 0.018;

/** Seconds for the echo to fall by 60 dB. */
const DECAY = 1.9;

/** How much of each seat is sent into the hall. */
const SEND = 0.6;

/** The whole sum is turned down by this before it reaches the limiter; -9.5 dB. */
const TRIM = 0.335;

/** Where the soft clipper starts to bend, as a fraction of full scale. */
const KNEE = 0.8;

/** How far over full scale the clipper still bends smoothly rather than flattening. */
const REACH = 2;

/** A pseudo-random sequence from a seed, so the hall is the same hall every time. */
function noise(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 2 ** 31 - 1;
  };
}

/** A hall's echo: noise that dies away and grows darker as it does, one stream per ear. */
function hall(context: BaseAudioContext): AudioBuffer {
  const rate = context.sampleRate;
  const length = Math.round(RING * rate);
  const buffer = context.createBuffer(2, length, rate);
  const delay = Math.round(PREDELAY * rate);
  for (let channel = 0; channel < 2; channel += 1) {
    const next = noise(0x5eed + channel);
    const wave = new Float32Array(length);
    let dark = 0;
    for (let frame = delay; frame < length; frame += 1) {
      const t = (frame - delay) / rate;
      // A one-pole low-pass whose corner falls as the echo ages: high notes die first.
      const open = 0.85 * Math.exp(-t / 0.6) + 0.12;
      dark += open * (next() - dark);
      wave[frame] = dark * Math.exp((-6.9 * t) / DECAY);
    }
    buffer.copyToChannel(wave, channel);
  }
  return buffer;
}

/** A curve that passes `x` unchanged below {@link KNEE} and bends smoothly towards 1 above. */
function softClip(): Float32Array<ArrayBuffer> {
  const points = 8193;
  const curve = new Float32Array(points);
  for (let at = 0; at < points; at += 1) {
    // The shaper reads its input from -1 to 1; it is fed the signal divided by REACH.
    const x = ((at / (points - 1)) * 2 - 1) * REACH;
    const size = Math.abs(x);
    const bent = size <= KNEE ? size : KNEE + (1 - KNEE) * Math.tanh((size - KNEE) / (1 - KNEE));
    curve[at] = Math.sign(x) * bent;
  }
  return curve;
}

/** The stage, the hall and the output stage of one audio context. */
export class Room {
  /** Where anything heard dry and in the middle goes: the metronome, the plain voice. */
  readonly dry: GainNode;
  private readonly send: GainNode;
  private readonly seats = new Map<Seat, StereoPannerNode>();

  constructor(private readonly context: BaseAudioContext) {
    this.dry = context.createGain();
    this.dry.gain.value = TRIM;

    // Threshold at full scale and no knee, so nothing under full scale is touched: a
    // compressor at the browser's defaults squeezed four strings' pp to ff from 12 dB to 3.
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = 0;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.15;

    const into = context.createGain();
    into.gain.value = 1 / REACH;
    const clipper = context.createWaveShaper();
    clipper.curve = softClip();
    clipper.oversample = "2x";

    this.dry.connect(limiter);
    limiter.connect(into);
    into.connect(clipper);
    clipper.connect(context.destination);

    const echo = context.createConvolver();
    echo.buffer = hall(context);
    this.send = context.createGain();
    this.send.gain.value = SEND;
    this.send.connect(echo);
    echo.connect(this.dry);
  }

  /** The input for everything played from `seat`: panned, heard dry and sent to the hall. */
  seat(seat: Seat): AudioNode {
    let panner = this.seats.get(seat);
    if (panner === undefined) {
      panner = this.context.createStereoPanner();
      panner.pan.value = seat;
      panner.connect(this.dry);
      panner.connect(this.send);
      this.seats.set(seat, panner);
    }
    return panner;
  }
}
